import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Input, Select, Tag, Space, Typography, message, Divider, Switch, Checkbox, Row, Col, Alert, AutoComplete } from 'antd';
import { ThunderboltOutlined, EditOutlined, UserOutlined, CrownOutlined } from '@ant-design/icons';
import { ROLE_OPTIONS } from './constants';

const { Text } = Typography;
const { Option } = Select;

const API_BASE_URL = '';

const BOSS_TYPES = [
  { value: 'HT', label: 'Horntail', color: '#fa8c16' },
  { value: 'PB', label: 'Pink Bean', color: '#eb2f96' },
];

// 角色定位选项 已迁移至 constants.js（保持单一数据源）


// 创建 Run 弹窗（包含添加第一个 Host 角色）
export const CreateRunModal = ({ visible, onCancel, onSuccess, existingRuns, maxRuns, currentUser }) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  
  const bossType = Form.useWatch('bossType', form);
  const isPB = bossType === 'PB';
  const isNT = bossType === 'NT';

  // 重置表单
  useEffect(() => {
    if (visible) {
      form.resetFields();
      form.setFieldsValue({ 
        bossType: 'HT',
        team: 1,
        isLeader: true,
        roles: ['host', 'attacker'],
        round1: true,
        round2: true
      });
    }
  }, [visible, form]);

  // 当 Boss 类型变化时，调整 Round 和队伍
  useEffect(() => {
    if (isPB) {
      form.setFieldsValue({ round2: false });
    }
    if (isNT) {
      // NT 模式：不需要 Round 选择，固定队伍 1
      form.setFieldsValue({ round1: true, round2: false, team: 1 });
    }
  }, [isPB, isNT, form]);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      const runName = (values.name || '').trim();

      // 确保 Host 角色在 roles 中
      if (!values.roles?.includes('host')) {
        values.roles = ['host', ...(values.roles || [])];
      }

      setLoading(true);

      const createdBy = currentUser?.username || '';

      // 1. 创建 Run
      const res = await fetch(`${API_BASE_URL}/api/runs?name=${encodeURIComponent(runName)}&boss_type=${values.bossType}&created_by=${encodeURIComponent(createdBy)}`, {
        method: 'POST'
      });
      
      if (!res.ok) {
        const data = await res.json();
        message.error(data.detail || '创建失败');
        setLoading(false);
        return;
      }
      
      const runData = await res.json();
      const newRun = runData.run;
      
      // 2. 使用增量 API 添加第一个角色作为 Host
      const hostCharacter = {
        name: values.hostCharacter.trim(),
        team: values.team || 1,
        isLeader: values.isLeader !== false,
        roles: values.roles || ['host', 'attacker'],
        round1: values.round1 !== false,
        round2: isPB ? false : (values.round2 !== false),
        needPendant: false,
        needChair: false,
        needCard: false,
        specialNeeds: []
      };
      
      // 使用增量 API 添加角色
      const addRes = await fetch(`${API_BASE_URL}/api/runs/${newRun.id}/characters?username=${encodeURIComponent(currentUser?.username || '')}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ character: hostCharacter, version: newRun.version || 1 })
      });
      
      if (addRes.ok) {
        const addData = await addRes.json();
        newRun.characters = [addData.character];
        newRun.version = addData.version;
        message.success(`Run "${runName}" 创建成功，${values.hostCharacter} 已设为 Host`);
        form.resetFields();
        onSuccess(newRun);
      } else {
        const errorData = await addRes.json();
        // 如果添加角色失败，删除已创建的 Run
        await fetch(`${API_BASE_URL}/api/runs/${newRun.id}?username=${encodeURIComponent(currentUser?.username || '')}`, { method: 'DELETE' });
        
        if (errorData.detail?.error === 'TEAM_FULL') {
          message.error(errorData.detail.message);
        } else {
          message.error('添加 Host 角色失败，Run 创建已取消');
        }
      }
    } catch (e) {
      if (e.errorFields) return;
      message.error('创建失败: ' + e.message);
    }
    setLoading(false);
  };

  // 过滤角色选项，确保 host 始终被选中
  const filteredRoleOptions = useMemo(() => {
    return ROLE_OPTIONS.filter(r => r.value !== 'host');
  }, []);

  // 获取当前用户已绑定的角色列表
  const userCharacterOptions = useMemo(() => {
    if (!currentUser?.characters) return [];
    return currentUser.characters.map(char => ({
      value: char,
      label: (
        <Space>
          <img 
            src={`${API_BASE_URL}/api/getavatar?name=${encodeURIComponent(char)}`}
            alt=""
            style={{ width: 24, height: 24, borderRadius: 4 }}
            onError={e => e.target.style.display = 'none'}
          />
          {char}
          <Tag color="green" style={{ fontSize: 10 }}>已绑定</Tag>
        </Space>
      )
    }));
  }, [currentUser?.characters]);

  return (
    <Modal
      title={<><ThunderboltOutlined /> 创建新 Run</>}
      open={visible}
      onCancel={onCancel}
      onOk={handleCreate}
      confirmLoading={loading}
      okText="创建 Run"
      cancelText="取消"
      width={500}
    >
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">
          当前已有 {existingRuns} / {maxRuns} 个 Run 进行中
        </Text>
      </div>
      
      <Form form={form} layout="vertical" initialValues={{ bossType: 'HT', team: 1, isLeader: true, roles: ['host', 'attacker'], round1: true, round2: true }}>
        <Form.Item
          name="name"
          label="Run 名称"
          rules={[{ required: true, message: '请输入 Run 名称' }]}
        >
          <Input placeholder="例如：HT R1-R2 周日场" />
        </Form.Item>
        
        <Form.Item
          name="bossType"
          label="Boss 类型"
          rules={[{ required: true, message: '请选择 Boss 类型' }]}
        >
          <Select>
            {BOSS_TYPES.map(boss => (
              <Option key={boss.value} value={boss.value} disabled={boss.disabled}>
                <Space>
                  <Tag color={boss.disabled ? '#d9d9d9' : boss.color}>{boss.value}</Tag>
                  {boss.label}
                  {boss.disabled && <Text type="secondary" style={{ fontSize: 12 }}>(暂不可用)</Text>}
                </Space>
              </Option>
            ))}
          </Select>
        </Form.Item>
        
        <Divider style={{ margin: '16px 0' }}>
          <Space>
            <CrownOutlined style={{ color: '#faad14' }} />
            <span>Host 角色信息</span>
          </Space>
        </Divider>
        
        <Alert 
          message="创建 Run 时必须添加第一个角色作为 Host" 
          type="info" 
          showIcon 
          style={{ marginBottom: 16 }}
        />
        
        <Form.Item
          name="hostCharacter"
          label="Host 角色名"
          rules={[{ required: true, message: '请输入或选择 Host 角色名' }]}
        >
          <AutoComplete
            options={userCharacterOptions}
            filterOption={(inputValue, option) =>
              option.value.toLowerCase().includes(inputValue.toLowerCase())
            }
            style={{ width: '100%' }}
          >
            <Input prefix={<UserOutlined />} placeholder="输入角色名或选择已绑定角色" maxLength={12} />
          </AutoComplete>
        </Form.Item>
        
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="team" label="队伍">
              {isNT ? (
                <Select disabled value={1}>
                  <Option value={1}>队伍 1</Option>
                </Select>
              ) : (
                <Select>
                  {[1, 2, 3, 4, 5].map(n => (
                    <Option key={n} value={n}>队伍 {n}</Option>
                  ))}
                </Select>
              )}
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="isLeader" label="队长" valuePropName="checked">
              <Switch checkedChildren={<CrownOutlined />} unCheckedChildren="否" />
            </Form.Item>
          </Col>
        </Row>
        
        <Form.Item 
          name="roles" 
          label={<Space><span>角色定位</span><Tag color="gold">Host (必选)</Tag></Space>}
        >
          <Select mode="multiple" placeholder="选择其他角色定位">
            {filteredRoleOptions.map(o => (
              <Option key={o.value} value={o.value}>
                <Tag color={o.color}>{o.label}</Tag>
              </Option>
            ))}
          </Select>
        </Form.Item>
        
        {!isNT && (
          <Form.Item label="参与 Round">
            <Space>
              <Form.Item name="round1" valuePropName="checked" noStyle>
                <Checkbox>Round 1</Checkbox>
              </Form.Item>
              <Form.Item name="round2" valuePropName="checked" noStyle>
                <Checkbox disabled={isPB}>Round 2 {isPB && '(PB 只有 1 Round)'}</Checkbox>
              </Form.Item>
            </Space>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

// 编辑 Run 名称弹窗
export const EditRunNameModal = ({ visible, onCancel, onSuccess, currentRun, currentUser }) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  React.useEffect(() => {
    if (visible && currentRun) {
      form.setFieldsValue({ name: currentRun.name });
    }
  }, [visible, currentRun, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const runName = (values.name || '').trim();

      const res = await fetch(`${API_BASE_URL}/api/runs/${currentRun.id}?username=${encodeURIComponent(currentUser?.username || '')}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: runName })
      });

      if (res.ok) {
        message.success('Run 名称已更新');
        onSuccess(runName);
      } else {
        const data = await res.json();
        message.error(data.detail || '更新失败');
      }
    } catch (e) {
      if (e.errorFields) return;
      message.error('更新失败');
    }
    setLoading(false);
  };

  return (
    <Modal
      title={<><EditOutlined /> 编辑 Run 名称</>}
      open={visible}
      onCancel={onCancel}
      onOk={handleSave}
      confirmLoading={loading}
      okText="保存"
      cancelText="取消"
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Run 名称"
          rules={[{ required: true, message: '请输入 Run 名称' }]}
        >
          <Input placeholder="例如：HT R1-R2 周日场" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// Boss 类型标签
export const BossTypeTag = ({ bossType }) => {
  const boss = BOSS_TYPES.find(b => b.value === bossType) || BOSS_TYPES[0];
  return <Tag color={boss.color}>{boss.value}</Tag>;
};

// 默认状态选项（当配置未加载时使用）
const DEFAULT_STATUS_OPTIONS = {
  HT: [
    { value: 'NotDone', label: 'NotDone', color: '#d9d9d9' },
    { value: 'Pre-hd(L)', label: 'Pre-hd(L)', color: '#fadb14' },
    { value: 'Pre-hd(R)', label: 'Pre-hd(R)', color: '#fadb14' },
    { value: 'Legs', label: 'Legs', color: '#52c41a' },
    { value: 'Tail', label: 'Tail', color: '#52c41a' },
    { value: 'Wings', label: 'Wings', color: '#1890ff' },
    { value: 'Right-hd', label: 'Right-hd', color: '#1890ff' },
    { value: 'Left-hd', label: 'Left-hd', color: '#722ed1' },
    { value: 'Mid-hd', label: 'Mid-hd', color: '#722ed1' },
    { value: 'AllDone', label: 'AllDone', color: '#52c41a' },
    { value: 'Abandoned', label: 'Abandoned', color: '#ff4d4f' },
  ],
  NT: [
    { value: 'NotDone', label: 'NotDone', color: '#d9d9d9' },
    { value: 'AllDone', label: 'AllDone', color: '#52c41a' },
    { value: 'Abandoned', label: 'Abandoned', color: '#ff4d4f' },
  ],
  PB: [
    { value: 'NotDone', label: 'NotDone', color: '#d9d9d9' },
    { value: 'AllDone', label: 'AllDone', color: '#52c41a' },
    { value: 'Abandoned', label: 'Abandoned', color: '#ff4d4f' },
  ]
};

// 获取 Boss 状态选项 (支持从配置获取)
export const getBossStatusOptions = (bossType, bossConfig = null) => {
  // 如果有配置，从配置中获取
  if (bossConfig && bossConfig[bossType]) {
    return bossConfig[bossType].statuses.map(s => ({
      value: s.name,
      label: s.name,
      color: s.color,
      ratio: s.ratio
    }));
  }
  
  // 否则使用默认值
  return DEFAULT_STATUS_OPTIONS[bossType] || DEFAULT_STATUS_OPTIONS.HT;
};

// 加载 Boss 配置
export const loadBossConfig = async () => {
  try {
    const res = await fetch(`${API_BASE_URL}/api/boss-config`);
    if (res.ok) {
      return await res.json();
    }
  } catch (e) {
    console.error('加载 Boss 配置失败:', e);
  }
  return null;
};

export default CreateRunModal;